import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL, getAuthHeader } from '../../utils/config';

// Shared fetch so TopUsersCard and AdminInsights make one request between them.
let _cache = null, _cacheAt = 0, _inflight = null;
function useInsights() {
  const fresh = _cache && Date.now() - _cacheAt < 60000;
  const [data, setData] = useState(fresh ? _cache : null);
  useEffect(() => {
    if (fresh) return;
    if (!_inflight) {
      _inflight = axios.get(`${API_URL}/admin/stats/insights`, { headers: getAuthHeader() })
        .then(r => { _cache = r.data; _cacheAt = Date.now(); return r.data; })
        .catch(() => null)
        .finally(() => { _inflight = null; });
    }
    let alive = true;
    _inflight.then(d => { if (alive && d) setData(d); });
    return () => { alive = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return data;
}

const cardStyle = dark => ({
  background: dark ? 'rgba(255,255,255,0.03)' : 'white',
  border: dark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
});
const fmtSize = b => { b = Number(b) || 0; if (b >= 1e9) return (b / 1e9).toFixed(2) + ' GB'; if (b >= 1e6) return (b / 1e6).toFixed(1) + ' MB'; if (b >= 1e3) return Math.round(b / 1e3) + ' KB'; return b + ' B'; };
const fmtNum = n => (Number(n) || 0).toLocaleString('en-IN');
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const GRAD = 'linear-gradient(90deg,#0d9488,#2563eb)';

const Title = ({ children, sub }) => (
  <div className="mb-4">
    <p className="font-black text-gray-800 dark:text-white text-sm">{children}</p>
    {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
  </div>
);
const Empty = () => <p className="text-xs text-gray-400 text-center py-4">No data yet</p>;
const Bar = ({ v, max, color }) => (
  <div className="h-1.5 rounded-full bg-gray-100 dark:bg-white/5 w-full overflow-hidden">
    <div className="h-full rounded-full" style={{ width: `${Math.max((v / Math.max(max, 1)) * 100, 2)}%`, background: color || GRAD }} />
  </div>
);
const Avatar = ({ name }) => (
  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-teal-400 to-blue-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
    {name?.[0]?.toUpperCase()}
  </div>
);

// ── Top users with 7d / 30d / All time tabs (replaces the old Top Users (7d) card)
export function TopUsersCard({ darkMode, stats7d }) {
  const ins = useInsights();
  const [tab, setTab] = useState('7d');
  const rows = (tab === '7d' ? (ins?.top_users_7d || stats7d) : tab === '30d' ? ins?.top_users_30d : ins?.top_users_all) || [];
  const max = rows[0]?.msg_count || 1;
  const tabs = [['7d', '7 days'], ['30d', '30 days'], ['all', 'All time']];
  return (
    <div className="rounded-2xl p-5" style={cardStyle(darkMode)}>
      <div className="flex items-center justify-between mb-4 gap-2">
        <p className="font-black text-gray-800 dark:text-white text-sm">Top Users</p>
        <div className="flex rounded-lg p-0.5 bg-gray-100 dark:bg-white/5">
          {tabs.map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`px-2 py-0.5 text-[11px] font-bold rounded-md transition ${tab === k ? 'bg-white dark:bg-white/10 text-teal-600 dark:text-teal-300 shadow-sm' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-200'}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="space-y-2.5 max-h-72 overflow-y-auto pr-1">
        {rows.map((u, i) => (
          <div key={i} className="flex items-center gap-2.5">
            <span className="text-xs font-black text-gray-400 w-5">{i + 1}</span>
            <Avatar name={u.full_name} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-bold text-gray-700 dark:text-gray-200 truncate">{u.full_name}</p>
                <span className="text-xs text-gray-400 flex-shrink-0">{fmtNum(u.msg_count)}</span>
              </div>
              <div className="mt-1"><Bar v={u.msg_count} max={max} /></div>
            </div>
          </div>
        ))}
        {!rows.length && (ins || tab === '7d' ? <Empty /> : <p className="text-xs text-gray-400 text-center py-4">Loading…</p>)}
      </div>
    </div>
  );
}

// ── Extra dashboard cards
export default function AdminInsights({ darkMode }) {
  const d = useInsights();
  const [showInactive, setShowInactive] = useState(false);
  if (!d) return null;

  const groups = d.top_groups || [];
  const teams = d.by_team || [];
  const heat = d.heatmap || [];
  const sharers = d.top_sharers || [];
  const ftypes = d.file_types || [];
  const plat = d.platforms || {};
  const ina = d.inactive || {};

  const heatMax = Math.max(1, ...heat.map(h => h.count));
  const cell = (dow, hr) => heat.find(h => h.dow === dow && h.hour === hr)?.count || 0;
  let peak = null; heat.forEach(h => { if (!peak || h.count > peak.count) peak = h; });
  const teamMax = Math.max(1, ...teams.map(t => t.messages_30d));
  const platTotal = Math.max(1, (plat.android || 0) + (plat.ios || 0) + (plat.web || 0));

  return (
    <div className="space-y-4" style={{ gridColumn: '1 / -1' }}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Most active groups */}
        <div className="rounded-2xl p-5" style={cardStyle(darkMode)}>
          <Title sub="Last 30 days">Most Active Groups</Title>
          <div className="space-y-3">
            {groups.map((g, i) => (
              <div key={i}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <p className="text-xs font-bold text-gray-700 dark:text-gray-200 truncate">{g.name || 'Unnamed group'}</p>
                  <span className="text-xs text-gray-400 flex-shrink-0">{fmtNum(g.msg_count)} · {g.members} members</span>
                </div>
                <Bar v={g.msg_count} max={groups[0]?.msg_count} />
              </div>
            ))}
            {!groups.length && <Empty />}
          </div>
        </div>

        {/* Activity by team */}
        <div className="rounded-2xl p-5" style={cardStyle(darkMode)}>
          <Title sub="Users · messages in last 30 days">Activity by Team</Title>
          <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
            {teams.map((t, i) => (
              <div key={i}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <p className="text-xs font-bold text-gray-700 dark:text-gray-200 truncate">{t.team}</p>
                  <span className="text-xs text-gray-400 flex-shrink-0">{t.users} users · {fmtNum(t.messages_30d)}</span>
                </div>
                <Bar v={t.messages_30d} max={teamMax} color="linear-gradient(90deg,#7c3aed,#2563eb)" />
              </div>
            ))}
            {!teams.length && <Empty />}
          </div>
        </div>

        {/* Web vs mobile */}
        <div className="rounded-2xl p-5" style={cardStyle(darkMode)}>
          <Title sub="Users with notifications registered">Where People Use Qtalk</Title>
          <div className="flex h-3 rounded-full overflow-hidden mb-4">
            <div style={{ width: `${(plat.android || 0) / platTotal * 100}%`, background: '#16a34a' }} />
            <div style={{ width: `${(plat.ios || 0) / platTotal * 100}%`, background: '#2563eb' }} />
            <div style={{ width: `${(plat.web || 0) / platTotal * 100}%`, background: '#f59e0b' }} />
          </div>
          {[['Android', plat.android, '#16a34a'], ['iOS', plat.ios, '#2563eb'], ['Web browser', plat.web, '#f59e0b']].map(([l, v, c]) => (
            <div key={l} className="flex items-center justify-between py-1.5">
              <span className="flex items-center gap-2 text-xs font-bold text-gray-700 dark:text-gray-200"><span className="w-2.5 h-2.5 rounded-full" style={{ background: c }} />{l}</span>
              <span className="text-xs text-gray-400">{fmtNum(v)} users</span>
            </div>
          ))}
          {plat.unknown > 0 && <p className="text-[11px] text-gray-400 mt-2">{plat.unknown} mobile users on older app versions (platform not recorded)</p>}
        </div>
      </div>

      {/* Busiest hours heatmap */}
      <div className="rounded-2xl p-5" style={cardStyle(darkMode)}>
        <div className="flex items-start justify-between gap-3">
          <Title sub="Messages by weekday and hour · last 30 days">Busiest Hours</Title>
          {peak && <span className="text-xs text-gray-400">Peak: <b className="text-teal-600 dark:text-teal-300">{DAYS[peak.dow - 1]} {String(peak.hour).padStart(2, '0')}:00</b> ({fmtNum(peak.count)})</span>}
        </div>
        <div className="overflow-x-auto">
          <div style={{ display: 'grid', gridTemplateColumns: '36px repeat(24, minmax(14px, 1fr))', gap: 3, minWidth: 520 }}>
            <div />
            {Array.from({ length: 24 }, (_, h) => <div key={h} className="text-[9px] text-gray-400 text-center">{h % 3 === 0 ? h : ''}</div>)}
            {DAYS.map((day, di) => (
              <React.Fragment key={day}>
                <div className="text-[10px] text-gray-400 flex items-center">{day}</div>
                {Array.from({ length: 24 }, (_, h) => {
                  const v = cell(di + 1, h);
                  return <div key={h} title={`${day} ${String(h).padStart(2, '0')}:00 — ${fmtNum(v)} messages`}
                    className="rounded-sm" style={{ height: 16, background: v ? `rgba(13,148,136,${0.12 + 0.88 * v / heatMax})` : (darkMode ? 'rgba(255,255,255,0.04)' : '#f1f5f9') }} />;
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Inactive users */}
        <div className="rounded-2xl p-5" style={cardStyle(darkMode)}>
          <Title sub="Active accounts only">User Health</Title>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <button onClick={() => setShowInactive(s => !s)} className="text-left rounded-xl p-3 bg-amber-50 dark:bg-amber-500/10 hover:ring-2 ring-amber-400 transition">
              <p className="text-2xl font-black text-amber-600 dark:text-amber-400">{fmtNum(ina.inactive_30d)}</p>
              <p className="text-[11px] font-bold text-gray-600 dark:text-gray-300">Not seen in 30+ days</p>
              <p className="text-[10px] text-gray-400">{showInactive ? 'Hide list' : 'Click to view'}</p>
            </button>
            <div className="rounded-xl p-3 bg-rose-50 dark:bg-rose-500/10">
              <p className="text-2xl font-black text-rose-600 dark:text-rose-400">{fmtNum(ina.profile_incomplete)}</p>
              <p className="text-[11px] font-bold text-gray-600 dark:text-gray-300">Profile not completed</p>
            </div>
          </div>
          {showInactive && (
            <div className="max-h-56 overflow-y-auto divide-y divide-gray-100 dark:divide-white/5">
              {(d.inactive_list || []).map((u, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 gap-2">
                  <span className="text-xs text-gray-700 dark:text-gray-200 truncate">{u.full_name}<span className="text-gray-400"> · {u.team || '—'}</span></span>
                  <span className="text-[11px] text-gray-400 flex-shrink-0">{u.last_seen ? new Date(u.last_seen).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—'}</span>
                </div>
              ))}
              {!(d.inactive_list || []).length && <Empty />}
            </div>
          )}
        </div>

        {/* Top file sharers */}
        <div className="rounded-2xl p-5" style={cardStyle(darkMode)}>
          <Title sub="By storage used · all time">Top File Sharers</Title>
          <div className="space-y-2.5">
            {sharers.map((u, i) => (
              <div key={i} className="flex items-center gap-2.5">
                <span className="text-xs font-black text-gray-400 w-4">{i + 1}</span>
                <Avatar name={u.full_name} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-bold text-gray-700 dark:text-gray-200 truncate">{u.full_name}</p>
                    <span className="text-xs text-gray-400 flex-shrink-0">{fmtSize(u.size)}</span>
                  </div>
                  <div className="mt-1"><Bar v={Number(u.size)} max={Number(sharers[0]?.size)} color="linear-gradient(90deg,#f59e0b,#ea580c)" /></div>
                  <p className="text-[10px] text-gray-400 mt-0.5">{fmtNum(u.files)} files</p>
                </div>
              </div>
            ))}
            {!sharers.length && <Empty />}
          </div>
        </div>

        {/* Storage by file type */}
        <div className="rounded-2xl p-5" style={cardStyle(darkMode)}>
          <Title sub="All time">Storage by File Type</Title>
          <div className="space-y-3">
            {ftypes.map((f, i) => (
              <div key={i}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <p className="text-xs font-bold text-gray-700 dark:text-gray-200">{f.type}</p>
                  <span className="text-xs text-gray-400">{fmtSize(f.size)} · {fmtNum(f.count)} files</span>
                </div>
                <Bar v={Number(f.size)} max={Math.max(...ftypes.map(x => Number(x.size)))} color="linear-gradient(90deg,#db2777,#7c3aed)" />
              </div>
            ))}
            {!ftypes.length && <Empty />}
          </div>
        </div>
      </div>
    </div>
  );
}
